/**
 * Compose/Reply/Forward dialog for the web email view.
 *
 * Mirrors the Flutter ComposeMessageDialog in pipar/lib/widgets/email_mailbox_dialog.dart:
 * - New: empty form with contact picker
 * - Reply: pre-filled To + "Re: " subject + quoted original body
 * - Forward: pre-filled "Fwd: " subject + quoted original body (recipient blank)
 *
 * Sends via the backend /contacts/send_message route, which internally routes
 * PiPar → PiPar or externally via Mailgun based on the recipient domain.
 */

import { useState, useEffect, useCallback, useMemo } from 'preact/hooks'
import type { CloudContact } from '../types'
import {
  sendContactMessage,
  getEmailStatus,
  getUserProfile,
  loadUserSettings,
  listCloudContacts,
} from '../services/api'
import { getCurrentUser } from '../services/auth'

type Priority = 'normal' | 'high' | 'urgent'

export interface ComposeEmailDialogProps {
  /** Pre-fill the To field. Editable unless `recipientLocked` is true. */
  initialToEmail?: string
  initialToName?: string
  initialSubject?: string
  initialBody?: string
  /** When true, the To field is read-only (e.g. sending to a selected contact). */
  recipientLocked?: boolean
  onClose: (sent: boolean) => void
}

interface SenderIdentity {
  piparEmail: string
  displayName: string
  friendlyName: string
  signature: string
}

const EMPTY_IDENTITY: SenderIdentity = { piparEmail: '', displayName: '', friendlyName: '', signature: '' }

async function loadSenderIdentity(): Promise<SenderIdentity> {
  const identity: SenderIdentity = { ...EMPTY_IDENTITY }
  try {
    const mobile = await loadUserSettings('mobile')
    identity.piparEmail = (mobile.pipar_mail_address as string) || ''
    identity.friendlyName = (mobile.pipar_friendly_name as string) || ''
    identity.displayName = (mobile.user_name as string) || ''
    identity.signature = (mobile.email_signature as string) || ''
  } catch { /* ignore */ }

  if (!identity.piparEmail) {
    const uid = getCurrentUser()?.uid
    if (uid) {
      const status = await getEmailStatus(uid)
      if (status.enabled && status.address) identity.piparEmail = status.address
    }
  }

  if (!identity.displayName) {
    try {
      const profile = await getUserProfile()
      identity.displayName = profile.user_name || ''
    } catch { /* ignore */ }
  }

  return identity
}

export function ComposeEmailDialog({
  initialToEmail,
  initialToName,
  initialSubject,
  initialBody,
  recipientLocked: recipientLockedProp,
  onClose,
}: ComposeEmailDialogProps) {
  const [toEmail, setToEmail] = useState(initialToEmail || '')
  const [subject, setSubject] = useState(initialSubject || '')
  const [body, setBody] = useState(initialBody || '')
  const [priority, setPriority] = useState<Priority>('normal')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [identity, setIdentity] = useState<SenderIdentity>(EMPTY_IDENTITY)
  const [identityLoaded, setIdentityLoaded] = useState(false)
  const [showContacts, setShowContacts] = useState(false)

  useEffect(() => {
    loadSenderIdentity().then((id) => {
      setIdentity(id)
      setIdentityLoaded(true)
    })
  }, [])

  const recipientLocked = !!recipientLockedProp

  const handleSend = useCallback(async () => {
    const to = toEmail.trim()
    const trimmedBody = body.trim()
    if (!to || !to.includes('@')) {
      setError('Enter a valid recipient email')
      return
    }
    if (!trimmedBody) {
      setError('Message body cannot be empty')
      return
    }
    if (!identity.piparEmail) {
      setError('PiPar mail address is not enabled for your account')
      return
    }

    setSending(true)
    setError(null)

    const isPiparRecipient = to.toLowerCase().endsWith('piparapp.com')
    const displayName = isPiparRecipient && identity.friendlyName
      ? identity.friendlyName
      : (identity.displayName || identity.piparEmail.split('@')[0])

    let effectiveSubject = subject.trim() || '(no subject)'
    if (priority === 'urgent') effectiveSubject = `[URGENT] ${effectiveSubject}`
    else if (priority === 'high') effectiveSubject = `[HIGH PRIORITY] ${effectiveSubject}`

    let effectiveBody = trimmedBody
    if (!isPiparRecipient) {
      const signature = identity.signature || 'This message is sent from PiPar - https://piparapp.com'
      if (signature) effectiveBody = `${trimmedBody}\n\n-----------------------\n${signature}`
    }

    try {
      await sendContactMessage({
        userDisplayName: displayName,
        userPiparEmail: identity.piparEmail,
        toEmail: to,
        subject: effectiveSubject,
        message: effectiveBody,
        priority,
      })
      onClose(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send email')
      setSending(false)
    }
  }, [toEmail, subject, body, priority, identity, onClose])

  const handleCancel = useCallback(() => {
    if (sending) return
    onClose(false)
  }, [sending, onClose])

  const title = useMemo(() => {
    if (initialToName) return `Message to ${initialToName}`
    if (initialToEmail) return `Message to ${initialToEmail}`
    return 'New Message'
  }, [initialToName, initialToEmail])

  return (
    <div style={overlayStyle} onClick={handleCancel}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{'✉️'} {title}</div>
            {identityLoaded && !identity.piparEmail && (
              <div style={{ fontSize: 12, color: '#ff6b6b', marginTop: 4 }}>
                PiPar mail is not enabled. Enable it from the mobile app before sending.
              </div>
            )}
            {identity.piparEmail && (
              <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.5)', marginTop: 4 }}>
                From: {identity.piparEmail}
              </div>
            )}
          </div>
          <button type="button" style={closeBtnStyle} onClick={handleCancel} disabled={sending} aria-label="Close">
            {'✕'}
          </button>
        </div>

        <div style={contentAreaStyle}>
          <div style={fieldRowStyle}>
            <label style={labelStyle} htmlFor="compose-to">To</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                id="compose-to"
                type="email"
                value={toEmail}
                onInput={(e) => setToEmail((e.currentTarget as HTMLInputElement).value)}
                style={{ ...inputStyle, flex: 1, background: recipientLocked ? 'rgba(0,34,51,0.4)' : inputStyle.background }}
                disabled={sending || recipientLocked}
                placeholder="recipient@example.com"
              />
              {!recipientLocked && (
                <button
                  type="button"
                  onClick={() => setShowContacts(true)}
                  style={pickerBtnStyle}
                  disabled={sending}
                  title="Pick from contacts"
                >
                  Contacts
                </button>
              )}
            </div>
          </div>

          <div style={fieldRowStyle}>
            <label style={labelStyle} htmlFor="compose-subject">Subject</label>
            <input
              id="compose-subject"
              type="text"
              value={subject}
              onInput={(e) => setSubject((e.currentTarget as HTMLInputElement).value)}
              style={inputStyle}
              disabled={sending}
              maxLength={200}
              placeholder="Subject"
            />
          </div>

          <div style={fieldRowStyle}>
            <label style={labelStyle}>Priority</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <PriorityChip value="normal" label="Normal" color="#9e9e9e" selected={priority === 'normal'} onSelect={setPriority} />
              <PriorityChip value="high" label="High" color="#fb8c00" selected={priority === 'high'} onSelect={setPriority} />
              <PriorityChip value="urgent" label="Urgent" color="#e53935" selected={priority === 'urgent'} onSelect={setPriority} />
            </div>
          </div>

          <div style={{ ...fieldRowStyle, flex: 1, minHeight: 0 }}>
            <label style={labelStyle} htmlFor="compose-body">Message</label>
            <textarea
              id="compose-body"
              value={body}
              onInput={(e) => setBody((e.currentTarget as HTMLTextAreaElement).value)}
              style={textareaStyle}
              disabled={sending}
              placeholder="Write your message..."
            />
          </div>

          {error && <div style={errorStyle}>{error}</div>}
        </div>

        <div style={footerStyle}>
          <button type="button" style={cancelBtnStyle} onClick={handleCancel} disabled={sending}>
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            style={sendBtnStyle}
            onClick={handleSend}
            disabled={sending || !identity.piparEmail || !toEmail.trim() || !body.trim()}
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>

        {showContacts && (
          <ContactPickerOverlay
            onSelect={(email) => { setToEmail(email); setShowContacts(false) }}
            onClose={() => setShowContacts(false)}
          />
        )}
      </div>
    </div>
  )
}

// ── Priority chip ─────────────────────────────────────────────────

function PriorityChip({ value, label, color, selected, onSelect }: {
  value: Priority
  label: string
  color: string
  selected: boolean
  onSelect: (v: Priority) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      style={{
        padding: '4px 12px',
        borderRadius: 16,
        border: `1px solid ${selected ? color : 'rgba(250,250,250,0.25)'}`,
        background: selected ? `${color}33` : 'transparent',
        color: selected ? color : 'rgba(250,250,250,0.7)',
        fontSize: 12,
        fontWeight: selected ? 600 : 400,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  )
}

// ── Contact picker ────────────────────────────────────────────────

interface ContactPickerProps {
  onSelect: (email: string) => void
  onClose: () => void
}

function ContactPickerOverlay({ onSelect, onClose }: ContactPickerProps) {
  const [contacts, setContacts] = useState<CloudContact[]>([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')

  useEffect(() => {
    listCloudContacts().then((cs) => {
      setContacts(cs)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return contacts
      .filter((c) => !(c.isBlocked ?? c.is_blocked))
      .filter((c) => {
        const ext = (c.externalEmail || c.external_email || '') as string
        const pipar = (c.piparEmail || c.pipar_email || '') as string
        return !!ext || !!pipar
      })
      .filter((c) => {
        if (!q) return true
        const name = ((c.friendlyName || c.friendly_name || c.fullName || c.full_name || '') as string).toLowerCase()
        const ext = ((c.externalEmail || c.external_email || '') as string).toLowerCase()
        const pipar = ((c.piparEmail || c.pipar_email || '') as string).toLowerCase()
        return name.includes(q) || ext.includes(q) || pipar.includes(q)
      })
  }, [contacts, query])

  return (
    <div style={pickerOverlayStyle} onClick={onClose}>
      <div style={pickerModalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <span style={{ flex: 1, fontSize: 15, fontWeight: 600 }}>Pick a contact</span>
          <button type="button" style={closeBtnStyle} onClick={onClose}>{'✕'}</button>
        </div>
        <div style={{ padding: 12, borderBottom: '1px solid rgba(250,250,250,0.08)' }}>
          <input
            type="text"
            value={query}
            onInput={(e) => setQuery((e.currentTarget as HTMLInputElement).value)}
            style={inputStyle}
            placeholder="Search contacts"
            autoFocus
          />
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
          {loading && <div style={{ padding: 20, textAlign: 'center', color: 'rgba(250,250,250,0.4)' }}>Loading...</div>}
          {!loading && filtered.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: 'rgba(250,250,250,0.4)' }}>No contacts</div>
          )}
          {filtered.map((c) => {
            const name = (c.friendlyName || c.friendly_name || c.fullName || c.full_name || '') as string
            const ext = (c.externalEmail || c.external_email || '') as string
            const pipar = (c.piparEmail || c.pipar_email || '') as string
            const isPiparReceiver = (c.isPiparReceiver ?? c.is_pipar_receiver) as boolean | undefined
            const email = (isPiparReceiver && pipar) ? pipar : (ext || pipar)
            return (
              <button
                type="button"
                key={c.id}
                onClick={() => onSelect(email)}
                style={contactRowStyle}
              >
                <div style={{ fontWeight: 500 }}>{name || email || 'Unknown'}</div>
                <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.5)', marginTop: 2 }}>{email}</div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ── Helpers: build reply / forward bodies ─────────────────────────

function formatQuoteHeader(lines: Array<{ label: string; value: string }>): string {
  return lines
    .filter((l) => l.value && l.value.length > 0)
    .map((l) => `${l.label}: ${l.value}`)
    .join('\n')
}

/** Build the body for a reply to an inbound email. */
export function buildReplyBody(original: {
  senderDisplay?: string
  subject?: string
  receivedAt?: string
  bodyPlain?: string
}): string {
  const header = formatQuoteHeader([
    { label: 'From', value: original.senderDisplay || '' },
    { label: 'Subject', value: original.subject || '' },
    { label: 'Date', value: original.receivedAt || '' },
  ])
  return `\n\n---------- Original Message ----------\n${header}\n\n${original.bodyPlain || ''}`
}

/** Build the body for a forward of an inbound or sent email. */
export function buildForwardBody(original: {
  fromLabel: string
  fromValue?: string
  subject?: string
  date?: string
  body?: string
}): string {
  const header = formatQuoteHeader([
    { label: original.fromLabel, value: original.fromValue || '' },
    { label: 'Subject', value: original.subject || '' },
    { label: 'Date', value: original.date || '' },
  ])
  return `\n\n---------- Forwarded Message ----------\n${header}\n\n${original.body || ''}`
}

export function replySubject(original?: string): string {
  const s = (original || '').trim()
  if (!s) return 'Re: '
  return s.startsWith('Re: ') ? s : `Re: ${s}`
}

export function forwardSubject(original?: string): string {
  const s = (original || '').trim()
  if (!s) return 'Fwd: '
  return s.startsWith('Fwd: ') || s.startsWith('Fw: ') ? s : `Fwd: ${s}`
}

// ── Styles ────────────────────────────────────────────────────────

const overlayStyle: Record<string, string | number> = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
}

const modalStyle: Record<string, string | number> = {
  width: '90%',
  maxWidth: 720,
  height: '85vh',
  background: '#0a1f24',
  border: '1px solid rgba(250,250,250,0.1)',
  borderRadius: 12,
  color: '#fafafa',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
}

const headerStyle: Record<string, string | number> = {
  padding: '14px 18px',
  borderBottom: '1px solid rgba(250,250,250,0.1)',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  flexShrink: 0,
}

const closeBtnStyle: Record<string, string | number> = {
  background: 'transparent',
  border: 'none',
  color: 'rgba(250,250,250,0.5)',
  fontSize: 18,
  cursor: 'pointer',
  padding: '4px 8px',
}

const contentAreaStyle: Record<string, string | number> = {
  flex: 1,
  overflow: 'auto',
  padding: 18,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
}

const fieldRowStyle: Record<string, string | number> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
}

const labelStyle: Record<string, string | number> = {
  fontSize: 11,
  fontWeight: 500,
  color: 'rgba(250,250,250,0.5)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

const inputStyle: Record<string, string | number> = {
  padding: '8px 10px',
  background: 'rgba(0,51,68,0.6)',
  border: '1px solid rgba(250,250,250,0.1)',
  borderRadius: 6,
  color: '#fafafa',
  fontSize: 13,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
}

const textareaStyle: Record<string, string | number> = {
  ...inputStyle,
  resize: 'vertical',
  lineHeight: '1.5',
  whiteSpace: 'pre-wrap',
  flex: 1,
  minHeight: 220,
  fontFamily: 'inherit',
}

const pickerBtnStyle: Record<string, string | number> = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid rgba(45,165,160,0.4)',
  background: 'rgba(45,165,160,0.15)',
  color: '#2DA5A0',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
  whiteSpace: 'nowrap',
}

const errorStyle: Record<string, string | number> = {
  padding: '8px 12px',
  background: 'rgba(255,68,68,0.1)',
  border: '1px solid rgba(255,100,100,0.3)',
  borderRadius: 6,
  color: '#ff6b6b',
  fontSize: 12,
}

const footerStyle: Record<string, string | number> = {
  padding: '12px 18px',
  borderTop: '1px solid rgba(250,250,250,0.1)',
  display: 'flex',
  gap: 10,
  alignItems: 'center',
  flexShrink: 0,
}

const cancelBtnStyle: Record<string, string | number> = {
  padding: '8px 20px',
  border: '1px solid rgba(250,250,250,0.2)',
  borderRadius: 8,
  background: 'transparent',
  color: 'rgba(250,250,250,0.7)',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
}

const sendBtnStyle: Record<string, string | number> = {
  padding: '8px 24px',
  border: 'none',
  borderRadius: 8,
  background: 'rgba(45,165,160,0.3)',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
}

const pickerOverlayStyle: Record<string, string | number> = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
}

const pickerModalStyle: Record<string, string | number> = {
  width: '85%',
  maxWidth: 520,
  maxHeight: '80%',
  background: '#0a1f24',
  border: '1px solid rgba(250,250,250,0.12)',
  borderRadius: 10,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}

const contactRowStyle: Record<string, string | number> = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '10px 12px',
  marginBottom: 4,
  background: 'rgba(0,34,51,0.5)',
  border: '1px solid rgba(250,250,250,0.08)',
  borderRadius: 6,
  color: '#fafafa',
  cursor: 'pointer',
  fontSize: 13,
}
